import React, { useState, useEffect } from 'react';
import confetti from 'canvas-confetti';

// API Configuration - CHANGE THIS TO YOUR WORKER URL
const API_BASE_URL = 'https://timeline-annotator-api.niels-turley.workers.dev';

const MedicalAnnotationViewer = () => {
  // Auth & Projects
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [reviewerInfo, setReviewerInfo] = useState(null);
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [projectConfig, setProjectConfig] = useState(null);
  
  // Patient & Note Data
  const [patients, setPatients] = useState([]);
  const [currentPatientIndex, setCurrentPatientIndex] = useState(0);
  const [currentPatient, setCurrentPatient] = useState(null);
  const [notes, setNotes] = useState([]);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  
  // Annotation & Progress
  const [annotationValue, setAnnotationValue] = useState('');
  const [comments, setComments] = useState('');
  const [progress, setProgress] = useState(null);
  const [patientStatuses, setPatientStatuses] = useState({});
  
  // UI Controls
  const [isLoading, setIsLoading] = useState(false);
  const [showPatientList, setShowPatientList] = useState(false);
  const [showOnlyUnannotated, setShowOnlyUnannotated] = useState(false);
  const [timelineZoom, setTimelineZoom] = useState({ start: null, end: null });
  const [threshold, setThreshold] = useState(0.5);
  const [showThresholdOnly, setShowThresholdOnly] = useState(false);
  const [isEditingThreshold, setIsEditingThreshold] = useState(false);
  const [hasShownConfetti, setHasShownConfetti] = useState(false);
  const [showModels, setShowModels] = useState({});
  const [showTimelineEvents, setShowTimelineEvents] = useState(true);
  const [isDraggingTimeline, setIsDraggingTimeline] = useState(false);
  const [timelineDragStart, setTimelineDragStart] = useState(null);
  
  // Login Form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  // API Helper
  const apiCall = async (endpoint, options = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Request failed');
    }
    
    return response.json();
  };

  // Authentication
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      const data = await apiCall('/api/login', {
        method: 'POST',
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      });
      
      setToken(data.token);
      localStorage.setItem('auth_token', data.token);
      setReviewerInfo({ id: data.reviewer_id, name: data.name });
    } catch (error) {
      setLoginError(error.message);
    }
  };

  const handleLogout = () => {
    setToken(null);
    localStorage.removeItem('auth_token');
    setReviewerInfo(null);
    setProjects([]);
    setCurrentProject(null);
  };

  // Load Projects
  useEffect(() => {
    if (!token) return;
    
    const loadProjects = async () => {
      try {
        const data = await apiCall('/api/projects');
        setProjects(data.projects);
        
        if (data.projects.length === 1) {
          handleSelectProject(data.projects[0]);
        }
      } catch (error) {
        console.error('Failed to load projects:', error);
        if (error.message === 'Unauthorized' || error.message === 'Token expired') {
          handleLogout();
        }
      }
    };
    
    loadProjects();
  }, [token]);

  // Project Selection
  const handleSelectProject = async (project) => {
    setCurrentProject(project);
    setCurrentPatientIndex(0);
    
    const modelToggles = {};
    if (project.model_config) {
      project.model_config.forEach(model => {
        modelToggles[model.name] = true;
      });
    }
    setShowModels(modelToggles);
    
    try {
      const config = await apiCall(`/api/project/${project.project_id}`);
      setProjectConfig(config);
    } catch (error) {
      console.error('Failed to load project config:', error);
    }
  };

  // Load Project Data
  useEffect(() => {
    if (!token || !currentProject) return;
    
    const loadProjectData = async () => {
      try {
        const patientsData = await apiCall(`/api/project/${currentProject.project_id}/patients`);
        setPatients(patientsData.patients);
        await refreshPatientStatuses();
      } catch (error) {
        console.error('Failed to load project data:', error);
      }
    };
    
    loadProjectData();
  }, [token, currentProject]);

  const refreshPatientStatuses = async () => {
    if (!currentProject) return;
    
    try {
      const statusData = await apiCall(`/api/project/${currentProject.project_id}/patient-statuses`);
      setPatientStatuses(statusData.statuses || {});
    } catch (e) {
      console.error('Failed to load patient statuses:', e);
    }
  };

  // Load Patient Notes
  useEffect(() => {
    if (!token || !currentProject || patients.length === 0) return;
    
    const loadPatientData = async () => {
      const patientId = patients[currentPatientIndex];
      setCurrentPatient(patientId);
      setIsLoading(true);
      setTimelineZoom({ start: null, end: null });
      
      try {
        const data = await apiCall(
          `/api/project/${currentProject.project_id}/patient/${patientId}/notes`
        );

        const notesList = data.notes
          .map(n => ({ ...n, note_text: null }))
          .sort((a, b) => new Date(a.note_date) - new Date(b.note_date)); // Sort by date ascending
        setNotes(notesList);
        setTimelineEvents(data.timeline_events || []);
        setProgress(data.progress);
        
        if (data.progress && data.progress.last_viewed_note_id) {
          const note = notesList.find(n => n.note_id === data.progress.last_viewed_note_id);
          if (note) {
            await loadNoteText(note);
            
            if (data.progress.annotation_note_id === note.note_id) {
              setAnnotationValue(data.progress.annotation_value || '');
              setComments(data.progress.comments || '');
            } else {
              setAnnotationValue('');
              setComments('');
            }
            return;
          }
        }
        
        if (currentProject.model_config && currentProject.model_config.length > 0) {
          const firstHighProb = notesList.find(n => {
            return currentProject.model_config.some(model => 
              n[model.name] && n[model.name] > 0.5
            );
          });
          if (firstHighProb) {
            await loadNoteText(firstHighProb);
            setAnnotationValue('');
            setComments('');
            return;
          }
        }
        
        if (notesList.length > 0) {
          await loadNoteText(notesList[0]);
        }
        
        setAnnotationValue('');
        setComments('');
      } catch (error) {
        console.error('Failed to load patient data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadPatientData();
  }, [token, currentProject, patients, currentPatientIndex]);

  const loadNoteText = async (note) => {
    if (!note) return;
    
    if (note.note_text !== null) {
      setSelectedNote(note);
      return;
    }
    
    try {
      const data = await apiCall(`/api/note/${note.note_id}`);
      const updatedNote = { ...note, note_text: data.note_text };
      
      setNotes(prev => prev.map(n => 
        n.note_id === note.note_id ? updatedNote : n
      ));
      
      setSelectedNote(updatedNote);
    } catch (error) {
      console.error('Failed to load note text:', error);
    }
  };

  // Progress Tracking
  useEffect(() => {
    if (!token || !currentProject || !currentPatient || !selectedNote) return;
    
    const updateProgress = async () => {
      try {
        await apiCall('/api/progress', {
          method: 'POST',
          body: JSON.stringify({
            project_id: currentProject.project_id,
            patient_id: currentPatient,
            last_viewed_note_id: selectedNote.note_id
          })
        });
      } catch (error) {
        console.error('Failed to update progress:', error);
      }
    };
    
    updateProgress();
  }, [token, currentProject, currentPatient, selectedNote]);

  // Save Annotation
  const handleSaveAnnotation = async () => {
    if (!selectedNote || !annotationValue || !currentProject) return;

    try {
      await apiCall('/api/annotation', {
        method: 'POST',
        body: JSON.stringify({
          project_id: currentProject.project_id,
          patient_id: currentPatient,
          note_id: selectedNote.note_id,
          annotation_value: annotationValue,
          comments: comments
        })
      });

      const data = await apiCall(
        `/api/project/${currentProject.project_id}/patient/${currentPatient}/notes`
      );
      setProgress(data.progress);
      await refreshPatientStatuses();
    } catch (error) {
      console.error('Failed to save annotation:', error);
      alert('Failed to save annotation. Please try again.');
    }
  };

  // Navigation
  const handleNoteClick = async (note) => {
    await loadNoteText(note);
    
    if (progress && progress.annotation_note_id === note.note_id) {
      setAnnotationValue(progress.annotation_value || '');
      setComments(progress.comments || '');
    } else {
      setAnnotationValue('');
      setComments('');
    }
  };

  const handlePreviousNote = () => {
    if (!selectedNote || notes.length === 0) return;
    const currentIndex = notes.findIndex(n => n.note_id === selectedNote.note_id);
    if (currentIndex > 0) {
      handleNoteClick(notes[currentIndex - 1]);
    }
  };

  const handleNextNote = () => {
    if (!selectedNote || notes.length === 0) return;
    const currentIndex = notes.findIndex(n => n.note_id === selectedNote.note_id);
    if (currentIndex < notes.length - 1) {
      handleNoteClick(notes[currentIndex + 1]);
    }
  };

  const handlePrevPatient = () => {
    if (currentPatientIndex > 0) {
      setCurrentPatientIndex(currentPatientIndex - 1);
    }
  };

  const handleNextPatient = () => {
    if (currentPatientIndex < patients.length - 1) {
      setCurrentPatientIndex(currentPatientIndex + 1);
    }
  };

  const handleJumpToPatient = (patientId) => {
    const index = patients.findIndex(p => p === patientId);
    if (index !== -1) {
      setCurrentPatientIndex(index);
      setShowPatientList(false);
    }
  };

  // Timeline drag event listeners
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDraggingTimeline || !timelineDragStart || !timelineZoom.start) return;
      
      const allDates = notes.map(n => new Date(n.note_date));
      const absoluteMinDate = Math.min(...allDates);
      const absoluteMaxDate = Math.max(...allDates);
      
      // Use the ORIGINAL drag start position, not the updated one
      const currentMin = new Date(timelineDragStart.timelineStart).getTime();
      const currentMax = new Date(timelineDragStart.timelineEnd).getTime();
      const currentRange = currentMax - currentMin;
      
      // Calculate total movement from original drag start
      const totalDeltaX = e.clientX - timelineDragStart.startX;
      const timelineWidth = timelineDragStart.width;
      const pixelToTimeRatio = currentRange / timelineWidth;
      const timeShift = -totalDeltaX * pixelToTimeRatio; // Negative for natural dragging
      
      let newMin = currentMin + timeShift;
      let newMax = currentMax + timeShift;
      
      // Constrain to absolute bounds
      if (newMin < absoluteMinDate) {
        newMin = absoluteMinDate;
        newMax = newMin + currentRange;
      }
      if (newMax > absoluteMaxDate) {
        newMax = absoluteMaxDate;
        newMin = newMax - currentRange;
      }
      
      setTimelineZoom({
        start: new Date(newMin).toISOString().split('T')[0],
        end: new Date(newMax).toISOString().split('T')[0]
      });
    };

    const handleMouseUp = () => {
      setIsDraggingTimeline(false);
      setTimelineDragStart(null);
    };

    if (isDraggingTimeline) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDraggingTimeline, timelineDragStart, timelineZoom, notes]);

  // Keyboard Navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePreviousNote();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNextNote();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [notes, selectedNote]);

  // Confetti
  const completedCount = patients.filter(pid => patientStatuses[pid]?.hasDiagnosis).length;

  useEffect(() => {
    if (patients.length > 0 && completedCount === patients.length && !hasShownConfetti) {
      const duration = 3000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

      function randomInRange(min, max) {
        return Math.random() * (max - min) + min;
      }

      const interval = setInterval(function() {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          clearInterval(interval);
          return;
        }

        const particleCount = 50 * (timeLeft / duration);
        
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
        });
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
        });
      }, 250);

      setHasShownConfetti(true);
    }
  }, [patients.length, completedCount, hasShownConfetti]);

  useEffect(() => {
    setHasShownConfetti(false);
  }, [token, currentProject]);

  // Text Highlighting
  const highlightText = (text) => {
    if (!text || !projectConfig || !projectConfig.keywords || projectConfig.keywords.length === 0) {
      return text;
    }
    
    let highlighted = text;
    projectConfig.keywords.forEach((pattern, idx) => {
      try {
        const regex = new RegExp(`(${pattern})`, 'gi');
        highlighted = highlighted.replace(regex, `<mark key="${idx}">$1</mark>`);
      } catch (e) {
        console.error('Invalid regex pattern:', pattern);
      }
    });
    
    return <span dangerouslySetInnerHTML={{ __html: highlighted }} />;
  };

  // Timeline - see continuation in next message due to length
  const renderTimeline = () => {
    if (notes.length === 0) return null;
    
    const allDates = notes.map(n => new Date(n.note_date));
    const absoluteMinDate = Math.min(...allDates);
    const absoluteMaxDate = Math.max(...allDates);
    
    const minDate = timelineZoom.start ? new Date(timelineZoom.start).getTime() : absoluteMinDate;
    const maxDate = timelineZoom.end ? new Date(timelineZoom.end).getTime() : absoluteMaxDate;
    const dateRange = maxDate - minDate || 1;
    
    const getPosition = (dateStr) => {
      const date = new Date(dateStr);
      const timestamp = date.getTime();
      
      if (timestamp < minDate || timestamp > maxDate) return null;
      
      return ((timestamp - minDate) / dateRange) * 100;
    };

    const hasModels = currentProject?.model_config && currentProject.model_config.length > 0;
    
    const activeModels = hasModels ? currentProject.model_config.filter(model =>
      notes.some(n => n[model.name] !== null && n[model.name] !== undefined)
    ) : [];

    const shouldDisplayProb = (prob) => {
      if (!showThresholdOnly) return prob !== null && prob !== undefined;
      return prob !== null && prob !== undefined && prob >= threshold;
    };

    const visibleNotes = notes.filter(note => {
      if (!hasModels) return true;
      if (!showThresholdOnly) return true;
      
      return activeModels.some(model => 
        note[model.name] !== null && 
        note[model.name] !== undefined && 
        note[model.name] >= threshold
      );
    });

    const hasVisibleNotes = visibleNotes.length > 0;

    let yMin = 0, yMax = 1, yRange = 1;
    
    if (hasModels && activeModels.length > 0 && showThresholdOnly) {
      const allProbs = notes.flatMap(n => 
        activeModels.map(m => n[m.name]).filter(p => p !== null && p !== undefined)
      );
      
      if (allProbs.length > 0) {
        yMin = Math.max(0, Math.min(...allProbs) - 0.1);
        yMax = Math.min(1, Math.max(...allProbs) + 0.1);
        yRange = yMax - yMin || 1;
      }
    } else if (hasModels && activeModels.length > 0) {
      yMin = 0;
      yMax = 1;
      yRange = 1;
    }

    const getYPosition = (prob) => {
      if (!hasModels) return 50;
      return ((yMax - prob) / yRange) * 100;
    };

    const handleWheel = (e) => {
      e.preventDefault();
      
      // Trackpad horizontal scrolling (deltaX)
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && timelineZoom.start) {
        const currentMin = new Date(timelineZoom.start).getTime();
        const currentMax = new Date(timelineZoom.end).getTime();
        const currentRange = currentMax - currentMin;
        
        // Shift by deltaX
        const shiftAmount = (currentRange * 0.01) * e.deltaX; // Scale factor for smoother scrolling
        
        let newMin = currentMin + shiftAmount;
        let newMax = currentMax + shiftAmount;
        
        // Constrain to absolute bounds
        if (newMin < absoluteMinDate) {
          newMin = absoluteMinDate;
          newMax = newMin + currentRange;
        }
        if (newMax > absoluteMaxDate) {
          newMax = absoluteMaxDate;
          newMin = newMax - currentRange;
        }
        
        setTimelineZoom({
          start: new Date(newMin).toISOString().split('T')[0],
          end: new Date(newMax).toISOString().split('T')[0]
        });
        return;
      }
      
      // Horizontal scrolling with Shift key
      if (e.shiftKey && timelineZoom.start) {
        const currentMin = new Date(timelineZoom.start).getTime();
        const currentMax = new Date(timelineZoom.end).getTime();
        const currentRange = currentMax - currentMin;
        
        // Shift by 10% of current range
        const shiftAmount = (currentRange * 0.1) * Math.sign(e.deltaY);
        
        let newMin = currentMin + shiftAmount;
        let newMax = currentMax + shiftAmount;
        
        // Constrain to absolute bounds
        if (newMin < absoluteMinDate) {
          newMin = absoluteMinDate;
          newMax = newMin + currentRange;
        }
        if (newMax > absoluteMaxDate) {
          newMax = absoluteMaxDate;
          newMin = newMax - currentRange;
        }
        
        setTimelineZoom({
          start: new Date(newMin).toISOString().split('T')[0],
          end: new Date(newMax).toISOString().split('T')[0]
        });
        return;
      }
      
      // Zoom in/out
      const delta = e.deltaY;
      const zoomFactor = delta > 0 ? 1.2 : 0.8;
      
      const currentMin = timelineZoom.start ? new Date(timelineZoom.start).getTime() : absoluteMinDate;
      const currentMax = timelineZoom.end ? new Date(timelineZoom.end).getTime() : absoluteMaxDate;
      const currentRange = currentMax - currentMin;
      const newRange = currentRange * zoomFactor;
      
      // Minimum zoom range (1 day in milliseconds)
      const minRange = 24 * 60 * 60 * 1000;
      
      // Get cursor position relative to timeline
      const rect = e.currentTarget.getBoundingClientRect();
      const cursorX = e.clientX - rect.left;
      const cursorRatio = cursorX / rect.width;
      
      // Calculate cursor's time position
      const cursorTime = currentMin + (currentRange * cursorRatio);
      
      // Calculate new bounds centered on cursor position
      let newMin = cursorTime - (newRange * cursorRatio);
      let newMax = cursorTime + (newRange * (1 - cursorRatio));
      
      // Zooming out - check if we should reset to full view
      if (delta > 0 && newRange >= (absoluteMaxDate - absoluteMinDate)) {
        setTimelineZoom({ start: null, end: null });
        return;
      }
      
      // Zooming in - enforce minimum range
      if (delta < 0 && newRange < minRange) {
        return; // Don't zoom in further
      }
      
      // Constrain to absolute bounds
      if (newMin < absoluteMinDate) {
        newMin = absoluteMinDate;
        newMax = newMin + newRange;
      }
      if (newMax > absoluteMaxDate) {
        newMax = absoluteMaxDate;
        newMin = newMax - newRange;
      }
      
      setTimelineZoom({
        start: new Date(newMin).toISOString().split('T')[0],
        end: new Date(newMax).toISOString().split('T')[0]
      });
    };

    const handleMouseDown = (e) => {
      if (timelineZoom.start) {
        setIsDraggingTimeline(true);
        setTimelineDragStart({ 
          startX: e.clientX,  // Keep original start position
          timelineStart: timelineZoom.start, 
          timelineEnd: timelineZoom.end,
          width: e.currentTarget.offsetWidth
        });
      }
    };

    const annotatedNote = progress?.annotation_note_id 
      ? notes.find(n => n.note_id === progress.annotation_note_id)
      : null;

    return (
      <div className="space-y-4">
        {hasModels && activeModels.length > 0 && (
          <div className="flex flex-wrap gap-4 text-sm">
            {activeModels.map(model => (
              <label key={model.name} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={showModels[model.name] || false}
                  onChange={(e) => setShowModels(prev => ({
                    ...prev,
                    [model.name]: e.target.checked
                  }))}
                />
                <span style={{ color: model.color }}>{model.label}</span>
              </label>
            ))}
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showTimelineEvents}
                onChange={(e) => setShowTimelineEvents(e.target.checked)}
              />
              <span>Events</span>
            </label>
          </div>
        )}

        {hasModels && activeModels.length > 0 && (
          <div className="flex items-center gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showThresholdOnly}
                onChange={(e) => setShowThresholdOnly(e.target.checked)}
              />
              <span>Show only above threshold:</span>
            </label>
            <div>
              {isEditingThreshold ? (
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  value={threshold}
                  onChange={(e) => setThreshold(parseFloat(e.target.value) || 0)}
                  onBlur={() => setIsEditingThreshold(false)}
                  onKeyDown={(e) => e.key === 'Enter' && setIsEditingThreshold(false)}
                  className="w-16 border rounded px-1"
                  autoFocus
                />
              ) : (
                <span 
                  className="cursor-pointer hover:bg-gray-100 px-1 rounded"
                  onClick={() => setIsEditingThreshold(true)}
                >
                  {threshold.toFixed(1)}
                </span>
              )}
            </div>
            {timelineZoom.start && (
              <button
                onClick={() => setTimelineZoom({ start: null, end: null })}
                className="ml-4 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs"
              >
                Reset Zoom
              </button>
            )}
          </div>
        )}

        {!hasVisibleNotes ? (
          <div className="text-gray-500 text-sm mt-4 p-4 bg-gray-50 rounded border">
            No notes {showThresholdOnly ? 'above threshold' : 'to display'}. 
            {showThresholdOnly && ' Try lowering the threshold value or unchecking the filter.'}
          </div>
        ) : (
          <div 
            className={`relative bg-gray-50 border rounded ${hasModels ? 'h-32' : 'h-16'} ${
              timelineZoom.start ? 'cursor-move' : 'cursor-default'
            }`}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
          >
            {timelineZoom.start && (
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs text-gray-500 bg-white px-2 rounded border">
                Zoomed: {timelineZoom.start} to {timelineZoom.end} (scroll to zoom, shift+scroll or drag to pan)
              </div>
            )}
            
            {/* Rest of timeline rendering stays the same... */}
            
            {hasModels && activeModels.length > 0 && (
              <>
                <div className="absolute -left-8 top-0 text-xs text-gray-600">{yMax.toFixed(1)}</div>
                <div className="absolute -left-8 bottom-0 text-xs text-gray-600">{yMin.toFixed(1)}</div>
              </>
            )}
            
            {hasModels && !showThresholdOnly && threshold > 0 && threshold < 1 && (
              <div
                className="absolute left-0 right-0 border-t-2 border-dashed border-orange-400"
                style={{ top: `${getYPosition(threshold)}%` }}
                title={`Threshold: ${threshold.toFixed(1)}`}
              />
            )}
            
            {showTimelineEvents && timelineEvents.map((event, idx) => {
              const pos = getPosition(event.event_date);
              if (pos === null) return null;
              
              return (
                <div
                  key={`event-${idx}`}
                  className="absolute top-0 bottom-0 border-l-2 border-dashed border-red-400"
                  style={{ left: `${pos}%` }}
                  title={`${event.event_type}: ${event.event_label || event.event_date}`}
                />
              );
            })}
            
            {visibleNotes.map(note => {
              const xPos = getPosition(note.note_date);
              if (xPos === null) return null;
              
              return (
                <React.Fragment key={note.note_id}>
                  {hasModels ? (
                    activeModels.map(model => {
                      if (!showModels[model.name]) return null;
                      if (!shouldDisplayProb(note[model.name])) return null;
                      
                      return (
                        <div
                          key={`${note.note_id}-${model.name}`}
                          className={`absolute w-3 h-3 rounded-full cursor-pointer hover:ring-2 hover:ring-offset-1 ${
                            selectedNote?.note_id === note.note_id ? 'ring-4 ring-black ring-offset-2 z-10' : ''
                          } ${
                            annotatedNote?.note_id === note.note_id ? 
                            'after:content-["★"] after:absolute after:-top-4 after:left-0 after:text-yellow-500' : ''
                          }`}
                          style={{
                            left: `${xPos}%`,
                            top: `${getYPosition(note[model.name])}%`,
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: model.color,
                            borderColor: model.color
                          }}
                          onClick={() => handleNoteClick(note)}
                          title={`${model.label}: ${(note[model.name] * 100).toFixed(0)}% - ${note.note_date}`}
                        />
                      );
                    })
                  ) : (
                    <div
                      className={`absolute w-3 h-3 rounded-full bg-blue-600 cursor-pointer hover:ring-2 ring-blue-400 ${
                        selectedNote?.note_id === note.note_id ? 'ring-4 ring-black ring-offset-2 z-10' : ''
                      } ${
                        annotatedNote?.note_id === note.note_id ? 
                        'after:content-["★"] after:absolute after:-top-4 after:left-0 after:text-yellow-500' : ''
                      }`}
                      style={{
                        left: `${xPos}%`,
                        top: '50%',
                        transform: 'translate(-50%, -50%)'
                      }}
                      onClick={() => handleNoteClick(note)}
                      title={`${note.note_date}`}
                    />
                  )}
                </React.Fragment>
              );
            })}
            
            <div className="absolute -bottom-6 left-0 text-xs text-gray-600">
              {new Date(minDate).toISOString().split('T')[0]}
            </div>
            <div className="absolute -bottom-6 right-0 text-xs text-gray-600">
              {new Date(maxDate).toISOString().split('T')[0]}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Annotation Panel
  const renderAnnotationPanel = () => {
    if (!projectConfig || !projectConfig.annotation_config) return null;
    
    const config = projectConfig.annotation_config;
    
    if (config.type === 'radio') {
      return (
        <div className="space-y-2">
          {config.options.map(option => (
            <label key={option} className="flex items-center gap-2">
              <input
                type="radio"
                name="annotation"
                value={option}
                checked={annotationValue === option}
                onChange={(e) => setAnnotationValue(e.target.value)}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      );
    }
    
    if (config.type === 'checkbox') {
      const values = Array.isArray(annotationValue) ? annotationValue : [];
      
      return (
        <div className="space-y-2">
          {config.options.map(option => (
            <label key={option} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={values.includes(option)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setAnnotationValue([...values, option]);
                  } else {
                    setAnnotationValue(values.filter(v => v !== option));
                  }
                }}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      );
    }
    
    return (
      <input
        type="text"
        value={annotationValue}
        onChange={(e) => setAnnotationValue(e.target.value)}
        className="w-full border rounded px-3 py-2"
        placeholder="Enter annotation..."
      />
    );
  };

  // Patient List Modal
  const renderPatientListModal = () => {
    if (!showPatientList) return null;

    const filteredPatients = showOnlyUnannotated
      ? patients.filter(pid => !patientStatuses[pid]?.hasDiagnosis)
      : patients;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-auto">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">
              All Patients ({completedCount}/{patients.length} completed)
            </h3>
            <button onClick={() => setShowPatientList(false)} className="text-gray-500 hover:text-gray-700">
              ✕
            </button>
          </div>
          
          <div className="mb-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={showOnlyUnannotated}
                onChange={(e) => setShowOnlyUnannotated(e.target.checked)}
              />
              Show only unannotated patients
            </label>
          </div>
          
          <div className="grid grid-cols-4 gap-2">
            {filteredPatients.map(pid => {
              const status = patientStatuses[pid] || {};
              return (
                <button
                  key={pid}
                  onClick={() => handleJumpToPatient(pid)}
                  className={`px-2 py-1 rounded text-sm ${
                    pid === currentPatient ? 'ring-2 ring-blue-500' : ''
                  } ${
                    status.hasDiagnosis 
                      ? 'bg-green-100 text-green-800' 
                      : status.hasProgress
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {pid}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Login Screen
  if (!token) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <h2 className="text-2xl font-bold mb-6 text-gray-900">Medical Annotation Viewer</h2>
          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                className="w-full border rounded px-3 py-2"
                required
              />
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                className="w-full border rounded px-3 py-2"
                required
              />
            </div>
            {loginError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                {loginError}
              </div>
            )}
            <button type="submit" className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700">
              Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Project Selection Screen
  if (!currentProject) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-2xl w-full">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Select Project</h2>
              <p className="text-sm text-gray-600 mt-1">
                Logged in as: <span className="font-semibold">{reviewerInfo?.name || 'User'}</span>
              </p>
            </div>
            <button onClick={handleLogout} className="text-sm text-blue-600 hover:text-blue-800">
              Logout
            </button>
          </div>
          
          {projects.length === 0 ? (
            <div className="text-gray-600 text-center py-8">
              No projects assigned. Please contact your administrator.
            </div>
          ) : (
            <div className="space-y-3">
              {projects.map(project => (
                <button
                  key={project.project_id}
                  onClick={() => handleSelectProject(project)}
                  className="w-full p-4 border rounded-lg hover:bg-gray-50 text-left"
                >
                  <div className="font-semibold text-lg">{project.project_name}</div>
                  {project.description && (
                    <div className="text-sm text-gray-600 mt-1">{project.description}</div>
                  )}
                  <div className="text-sm text-gray-500 mt-2">
                    {project.completed_count}/{project.patient_count} patients completed
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Main Viewer
  if (patients.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-600">Loading patients...</div>
      </div>
    );
  }

  const annotatedNote = progress?.annotation_note_id 
    ? notes.find(n => n.note_id === progress.annotation_note_id)
    : null;

  return (
    <div className="flex h-screen bg-gray-100">
      {renderPatientListModal()}
      
      <div className="flex-1 flex flex-col p-4">
        {isLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
            <div className="text-gray-600">Loading patient data...</div>
          </div>
        )}
        
        <div className="flex-1 bg-white rounded-lg shadow p-6 mb-4 overflow-auto">
          <div className="mb-4 text-sm text-gray-600 flex justify-between items-center">
            <div>
              <span className="font-semibold">{currentProject.project_name}</span>
              <span className="mx-2">|</span>
              Patient: <span className="font-semibold">{currentPatient}</span>
              {selectedNote && (
                <span className="ml-4">
                  Note Date: <span className="font-semibold">{selectedNote.note_date}</span>
                </span>
              )}
            </div>
            {selectedNote && currentProject.model_config && currentProject.model_config.length > 0 && (
              <div className="flex gap-4 text-xs">
                {currentProject.model_config.map(model => (
                  selectedNote[model.name] !== null && selectedNote[model.name] !== undefined && (
                    <span key={model.name} style={{ color: model.color }}>
                      {model.label}: <span className="font-semibold">
                        {(selectedNote[model.name] * 100).toFixed(1)}%
                      </span>
                    </span>
                  )
                ))}
              </div>
            )}
          </div>
          
          {selectedNote ? (
            <div className="prose max-w-none">
              {highlightText(selectedNote.note_text)}
            </div>
          ) : (
            <div className="text-gray-400 text-center mt-8">
              Select a note from the timeline below
            </div>
          )}
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="font-semibold mb-2">Patient Timeline</h3>
          {renderTimeline()}
        </div>
      </div>
      
      <div className="w-80 bg-white shadow-lg p-6 flex flex-col">
        <div className="mb-4 pb-4 border-b space-y-2">
          <div className="text-sm text-gray-600">
            Logged in as: <span className="font-semibold">{reviewerInfo?.name || 'User'}</span>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
            <select
              value={currentProject.project_id}
              onChange={(e) => {
                const project = projects.find(p => p.project_id === e.target.value);
                if (project) handleSelectProject(project);
              }}
              className="w-full border rounded px-2 py-1 text-sm"
            >
              {projects.map(p => (
                <option key={p.project_id} value={p.project_id}>
                  {p.project_name}
                </option>
              ))}
            </select>
          </div>
          
          <button onClick={handleLogout} className="text-sm text-blue-600 hover:text-blue-800">
            Logout
          </button>
        </div>
        
        {annotatedNote && (
          <div 
            className="mb-4 p-3 bg-green-50 border border-green-200 rounded cursor-pointer hover:bg-green-100"
            onClick={() => handleNoteClick(annotatedNote)}
          >
            <div className="flex items-center gap-2 text-green-800 font-semibold">
              <span>✓</span>
              <span>Annotation Saved</span>
            </div>
            <div className="text-sm text-green-700 mt-1">
              Note: {annotatedNote.note_date}
            </div>
            <div className="text-xs text-green-600 mt-1">
              (click to view)
            </div>
          </div>
        )}
        
        <div className="mb-6">
          <h3 className="font-semibold mb-3">
            {projectConfig?.annotation_config?.type === 'radio' ? 'Annotation' : 
             projectConfig?.annotation_config?.type === 'checkbox' ? 'Select All That Apply' :
             'Annotation'}
          </h3>
          {renderAnnotationPanel()}
        </div>
        
        {projectConfig?.annotation_config?.allow_comments && (
          <div className="mb-6">
            <h3 className="font-semibold mb-2">Comments</h3>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm"
              rows="3"
              placeholder="Optional notes..."
            />
          </div>
        )}
        
        <button
          onClick={handleSaveAnnotation}
          disabled={!annotationValue}
          className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed mb-4"
        >
          Save Annotation
        </button>
        
        <div className="mt-auto pt-4 border-t">
          <div className="mb-2">
            <button
              onClick={() => setShowPatientList(true)}
              className="text-sm text-blue-600 hover:text-blue-800 mb-2 w-full text-left"
            >
              View all patients ({completedCount}/{patients.length} completed)
            </button>
            
            <div className="relative w-full h-2 bg-gray-200 rounded-full cursor-pointer"
                 onClick={() => setShowPatientList(true)}>
              <div
                className="absolute top-0 left-0 h-full bg-green-500 rounded-full"
                style={{ width: `${(completedCount / patients.length) * 100}%` }}
              />
            </div>
          </div>
          
          <div className="flex gap-2 mt-4">
            <button
              onClick={handlePrevPatient}
              disabled={currentPatientIndex === 0}
              className="flex-1 bg-gray-200 py-2 rounded hover:bg-gray-300 disabled:opacity-50"
            >
              ← Previous
            </button>
            <button
              onClick={handleNextPatient}
              disabled={currentPatientIndex === patients.length - 1}
              className="flex-1 bg-gray-200 py-2 rounded hover:bg-gray-300 disabled:opacity-50"
            >
              Next →
            </button>
          </div>
          
          <div className="mt-2 text-center text-sm text-gray-600">
            Patient {currentPatientIndex + 1} of {patients.length}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MedicalAnnotationViewer;
